import {useEffect} from "react";
import {App as AntdApp, Alert, Col, Row} from "antd";
import {useForm, Controller} from "react-hook-form";
import {Button, Loading} from "~/components";
import {InputField, SelectField, TextAreaField} from "~/components/Forms";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {useGetContentSettingsQuery, useUpdateContentSettingsMutation} from "~/reduxs/api/contentApiSlice";

const REVIEW_OPTIONS = [
	{value: 'review_all', label: 'Duyệt dàn ý + duyệt bài (an toàn nhất — mặc định)'},
	{value: 'review_article', label: 'Chỉ duyệt bài (AI tự viết theo dàn ý)'},
	{value: 'full_auto', label: 'Tự động hoàn toàn (không cần duyệt)'},
];

const IMAGE_OPTIONS = [
	{value: 'stock', label: 'Ảnh stock miễn phí (Pexels/Pixabay — mặc định)'},
	{value: 'ai', label: 'AI sinh ảnh (Gemini — cần khoá Gemini/AI_IMAGE_KEY; lỗi tự rơi về stock)'},
	{value: 'template', label: 'Chỉ ảnh đại diện template thương hiệu'},
	{value: 'off', label: 'Không chèn ảnh'},
];

const DEFAULTS = {
	brand_name: '', website_topic: '', audience: '', tone: '', pronoun: '',
	banned_words: '', required_phrases: '', cta: '', color: '', notes: '',
	review_mode: 'review_all', image_source: 'stock',
};

/**
 * Tab "Thương hiệu & Cài đặt" — Brand Guideline nạp vào MỌI prompt của pipeline
 * (dàn ý/viết/sửa) + cấu hình vận hành user tự cài đặt (quyết §9.7): chế độ duyệt
 * (review_mode) và nguồn ảnh (image_source).
 */
function ContentSettingsTab({projectId}) {

	const {message} = AntdApp.useApp();
	const {data, isLoading} = useGetContentSettingsQuery(projectId);
	const [save, {isLoading: saving}] = useUpdateContentSettingsMutation();

	const {control, handleSubmit, reset} = useForm({defaultValues: DEFAULTS});

	useEffect(() => {
		if (data) {
			reset({...DEFAULTS, ...(data.brand || {}), review_mode: data.review_mode || 'review_all', image_source: data.image_source || 'stock'});
		}
	}, [data, reset]);

	if (isLoading) return <Loading />;

	const onSubmit = async (values) => {
		try {
			await save({projectId, ...values}).unwrap();
			message.success('Đã lưu cài đặt Nội dung AI');
		} catch (e) {
			message.error(rtkErrorMessage(e));
		}
	};

	return (
		<form className="form" onSubmit={handleSubmit(onSubmit)} style={{maxWidth: 860}}>
			{data && data.stock_ready === false && (
				<Alert
					type="info" showIcon style={{marginBottom: 16}}
					message="Chưa cấu hình API ảnh stock"
					description="Thêm PEXELS_API_KEY hoặc PIXABAY_API_KEY (đăng ký miễn phí) vào backend/.env để bài viết có ảnh minh hoạ; chưa có thì hệ thống dùng ảnh đại diện template."
				/>
			)}

			<h4 style={{margin: '0 0 12px'}}>Brand Guideline — nạp vào mọi bài AI viết</h4>

			<Row gutter={16}>
				<Col xs={24} md={12}>
					<Controller control={control} name="brand_name" render={({field}) => (
						<InputField label="Tên thương hiệu" placeholder="vd: Omni SEO" {...field} />
					)} />
				</Col>
				<Col xs={24} md={12}>
					<Controller control={control} name="website_topic" render={({field}) => (
						<InputField label="Chủ đề website" placeholder="vd: blog công cụ SEO cho doanh nghiệp nhỏ" {...field} />
					)} />
				</Col>
				<Col xs={24} md={12}>
					<Controller control={control} name="audience" render={({field}) => (
						<InputField label="Đối tượng đọc" placeholder="vd: chủ shop online chưa rành kỹ thuật" {...field} />
					)} />
				</Col>
				<Col xs={24} md={12}>
					<Controller control={control} name="tone" render={({field}) => (
						<InputField label="Tông giọng" placeholder="vd: thân thiện, thực tế, không đao to búa lớn" {...field} />
					)} />
				</Col>
				<Col xs={24} md={12}>
					<Controller control={control} name="pronoun" render={({field}) => (
						<InputField label="Xưng hô" placeholder="vd: xưng 'chúng tôi', gọi người đọc là 'bạn'" {...field} />
					)} />
				</Col>
				<Col xs={24} md={12}>
					<Controller control={control} name="cta" render={({field}) => (
						<InputField label="Lời kêu gọi hành động (CTA)" placeholder="vd: Dùng thử Omni SEO miễn phí" {...field} />
					)} />
				</Col>
				<Col xs={24} md={12}>
					<Controller control={control} name="banned_words" render={({field}) => (
						<TextAreaField label="Từ CẤM dùng (mỗi dòng 1 từ)" rows={3} placeholder={"rẻ nhất\nsố 1 thị trường"} {...field} />
					)} />
				</Col>
				<Col xs={24} md={12}>
					<Controller control={control} name="required_phrases" render={({field}) => (
						<TextAreaField label="Cụm từ nên dùng (mỗi dòng 1 cụm)" rows={3} placeholder={"tối ưu bền vững\nSEO trắng"} {...field} />
					)} />
				</Col>
				<Col xs={24} md={12}>
					<Controller control={control} name="color" render={({field}) => (
						<InputField label="Màu thương hiệu (mã hex — dùng cho ảnh đại diện template)" placeholder="#4f46e5" {...field} />
					)} />
				</Col>
				<Col xs={24} md={12}>
					<Controller control={control} name="notes" render={({field}) => (
						<TextAreaField label="Ghi chú riêng cho AI (tuỳ chọn)" rows={3} placeholder="vd: luôn có phần ví dụ thực tế ở mỗi mục lớn" {...field} />
					)} />
				</Col>
			</Row>

			<h4 style={{margin: '8px 0 12px'}}>Vận hành pipeline</h4>

			<Row gutter={16}>
				<Col xs={24} md={12}>
					<Controller control={control} name="review_mode" render={({field}) => (
						<SelectField label="Chế độ duyệt" options={REVIEW_OPTIONS} style={{width: '100%'}} {...field} />
					)} />
				</Col>
				<Col xs={24} md={12}>
					<Controller control={control} name="image_source" render={({field}) => (
						<SelectField label="Nguồn ảnh (2 ảnh thân bài + 1 ảnh đại diện)" options={IMAGE_OPTIONS} style={{width: '100%'}} {...field} />
					)} />
				</Col>
			</Row>

			<div style={{marginTop: 8}}>
				<Button primary type="submit" loading={saving}>Lưu cài đặt</Button>
			</div>
		</form>
	);
}

export default ContentSettingsTab;
